import type { NativeSession } from "../native/session";
import type { ServerTemplate } from "./native-template-record";
import { NativeFiles, exportNativeImage } from "./native-files";
import { ImageStore } from "../render/image-store";
import { encodeStoredDocument } from "../storage/project-record";
import { blobDataURL } from "../adapters/blob-data-url";
import { templateMarkup } from "./native-template-markup";
import "./native-template-save.css";

/** Original administrator dialog; saves an independent, immutable engine document. */
export class NativeTemplateSave {
  private target: { record: ServerTemplate; generation: number } | undefined;
  private admin = false;
  private update = document.createElement("button");
  constructor(
    private files: NativeFiles,
    private session: NativeSession,
    private prepare: () => boolean,
    private refresh: () => Promise<void>,
    private message: (text: string, error?: boolean) => void,
  ) {
    const create = document.createElement("button");
    create.id = "native-save-template";
    create.textContent = "Сохранить в шаблоны";
    create.hidden = true;
    this.update.id = "native-update-template";
    this.update.textContent = "Обновить открытый шаблон";
    this.update.hidden = true;
    create.onclick = () => this.open();
    this.update.onclick = () => this.open(this.target?.record);
    document.querySelector(".save-menu-panel")!.append(create, this.update);
    void files
      .api("/session")
      .then((result) => {
        this.admin = !!result.user?.isAdmin;
        create.hidden = !this.admin;
        this.render();
      })
      .catch(() => {});
  }
  opened(record: ServerTemplate) {
    this.target = { record, generation: this.session.generation };
    this.render();
  }
  render() {
    this.update.hidden =
      !this.admin || this.target?.generation !== this.session.generation;
  }
  private open(target?: ServerTemplate) {
    if (
      !this.admin ||
      !this.prepare() ||
      (target && this.target?.generation !== this.session.generation)
    )
      return;
    this.files.closeMenu();
    const snapshot = structuredClone(this.session.document),
      generation = this.session.generation;
    const container = document.createElement("div");
    container.innerHTML = templateMarkup;
    const dialog = container.firstElementChild as HTMLDialogElement;
    const field = <T extends HTMLElement>(id: string) =>
      dialog.querySelector<T>("#" + id)!;
    field<HTMLInputElement>("template-title").value =
      target?.title || this.session.page.name || "Новый шаблон";
    field<HTMLTextAreaElement>("template-description").value =
      target?.description || "";
    field<HTMLInputElement>("template-tags").value =
      target?.tags.join(", ") || "";
    if (target) {
      dialog.querySelector("h2")!.textContent = "Обновить шаблон";
      field("template-save-submit").textContent = "Обновить шаблон";
    }
    field("template-save-close").onclick = field(
      "template-save-cancel",
    ).onclick = () => dialog.close();
    field<HTMLFormElement>("template-save-form").onsubmit = async (event) => {
      event.preventDefault();
      const submit = field<HTMLButtonElement>("template-save-submit"),
        error = field("template-save-error");
      if (submit.disabled) return;
      submit.disabled = true;
      error.textContent = "";
      try {
        if (generation !== this.session.generation)
          throw new Error(
            "Текущий проект изменился. Откройте сохранение шаблона ещё раз.",
          );
        // A shared template must not depend on media belonging to a deletable personal project.
        for (const asset of snapshot.assets) {
          if (asset.source.startsWith("data:")) continue;
          const url = new URL(asset.source, location.origin);
          if (
            url.origin !== location.origin ||
            !url.pathname.startsWith("/upload/creatorenginepro/")
          )
            throw new Error(
              "Ресурс шаблона находится вне хранилища редактора.",
            );
          const response = await fetch(url.href, {
            credentials: "same-origin",
          });
          if (!response.ok)
            throw new Error("Не удалось включить ресурс в шаблон.");
          asset.source = await blobDataURL(await response.blob());
        }
        const imageFile = field<HTMLInputElement>("template-preview-file")
          .files?.[0];
        const page = snapshot.pages.find(
          (p) => p.id === snapshot.activePageId,
        )!;
        const preview = imageFile
          ? await blobDataURL(imageFile)
          : await blobDataURL(
              await exportNativeImage(
                snapshot,
                "jpeg",
                new ImageStore(),
                Math.min(1, 600 / Math.max(page.size.width, page.size.height)),
              ),
            );
        if (!dialog.open) return;
        const result = await this.files.api("/templates", {
          action: target ? "update" : "create",
          ...(target ? { templateId: target.id } : {}),
          title: field<HTMLInputElement>("template-title").value.trim(),
          description: field<HTMLTextAreaElement>(
            "template-description",
          ).value.trim(),
          tags: field<HTMLInputElement>("template-tags")
            .value.split(/[,;]+/)
            .map((t) => t.trim())
            .filter(Boolean),
          previewDataUrl: preview,
          data: encodeStoredDocument(snapshot),
          isActive: target?.is_active ?? true,
        });
        if (generation === this.session.generation && result.template)
          this.opened(result.template);
        dialog.close();
        this.message(target ? "Шаблон обновлён." : "Шаблон сохранён.");
        await this.refresh();
      } catch (e) {
        error.textContent = (e as Error).message;
      } finally {
        submit.disabled = false;
      }
    };
    dialog.onclose = () => dialog.remove();
    document.getElementById("native-editor")!.append(dialog);
    dialog.showModal();
    field("template-title").focus();
  }
}
